import { 
  getQRScanStats, 
  getTotalUniqueVisitors 
} from './qrTrackingService';
import { 
  SalesData, 
  QRScan 
} from '../types/analytics';
import { getDatabase, ref, get, query, orderByChild, startAt, endAt } from 'firebase/database';
import type { TrackOrderData } from '../types/order.js';

export class AdminService {
  // Analytics
  /**
   * Aggregates orders/QR scans over the timeframe supplied by the caller.
   * NOTE: the dashboard currently passes a 7-day window while some overview
   * cards are labelled "24h"/"Today" — the labels are owned by
   * DashboardOverview; the aggregation itself correctly honours `timeframe`.
   */
  static async getDashboardData(timeframe: { start: Date; end: Date }): Promise<SalesData> {
    const db = getDatabase();
    const ordersRef = ref(db, 'orders');
    const ordersQuery = query(
      ordersRef,
      orderByChild('timestamp'),
      startAt(timeframe.start.getTime()),
      endAt(timeframe.end.getTime())
    );

    const snapshot = await get(ordersQuery);
    const orders: TrackOrderData[] = [];
    const itemCounts: { [key: string]: number } = {};
    let totalSales = 0;

    snapshot.forEach((child) => {
      const order = child.val() as TrackOrderData;
      orders.push(order);
      
      // Calculate total sales
      totalSales += typeof order.total === 'number' ? order.total : Number(order.total) || 0;

      // Count items
      (order.items ?? []).forEach((item: { name: string; quantity: number }) => {
        itemCounts[item.name] = (itemCounts[item.name] || 0) + item.quantity;
      });
    });

    // Get today's orders
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const ordersToday = orders.filter(order => order.timestamp >= today.getTime());

    // Get QR scan stats and the REAL unique-visitor KPI (sessions in the last
    // 24h). The old getUniqueVisitors('24') call passed '24' as a table
    // number, so the KPI always read ~0.
    const qrStats = await getQRScanStats(timeframe);
    const uniqueVisitors = await getTotalUniqueVisitors(24);

    // Format popular items
    const popularItems = Object.entries(itemCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    return {
      totalSales,
      ordersCount: ordersToday.length,
      qrScans: qrStats,
      popularItems,
      uniqueVisitors,
      recentOrders: [...ordersToday]
        .sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0))
        .slice(0, 5) // Newest orders first
    };
  }

  static async getQRScanStats(timeframe: { start: Date; end: Date }): Promise<QRScan[]> {
    return getQRScanStats(timeframe);
  }
} 
